import { reinigeBlockHtml } from "./blockHtml";
import { NEU, zuordnen, type Abschnitt, type AbschnittStand } from "./notizFeldModell";
import { aktivesDokument } from "../windowManager/browserFenster/fensterRegister";

/**
 * Das DOM des Notizfeldes — bauen, sauber halten, auslesen, abgleichen.
 *
 * Getrennt von der Komponente, weil `contentEditable` der unberechenbarste
 * Teil des Browsers ist: Was hier steht, lässt sich in jsdom Schritt für
 * Schritt prüfen, ohne React, ohne Ereignisse, ohne Zeit.
 *
 * ══ DAS ATTRIBUT IST ANZEIGE, NICHT INHALT ═════════════════════════════════
 * `data-nk` (welche Notizzeile), `data-kuerzel` (das Zeichen am Rand),
 * `data-zeit` (das Datum über der Notiz, T-407) und der
 * Platzhalter stehen NUR im Browser. Vor dem Speichern werden sie abgestreift — in die
 * Datenbank geht dasselbe HTML wie bisher, und `blockHtml.ts` muss sie nicht
 * erlauben.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * Was am Rand eines Abschnitts steht. `null` = kein Zeichen (Schreibstelle).
 * `zeit` steht klein über der Notiz (T-407) — fehlt sie, bleibt die Zeile leer.
 */
export type RandMarke = {
    kuerzel: string;
    farbe: string;
    titel: string;
    zeit?: string;
    /** Der Abschnitt spiegelt das reMarkable und ist nicht bearbeitbar. */
    gesperrt?: boolean;
} | null;

/** Kennzeichen eines gesperrten Abschnitts (reMarkable) — nur Anzeige, nie gespeichert. */
const GESPERRT_ATTR = "data-gesperrt";

const KEY_ATTR = "data-nk";

/**
 * Die Kinder, mit denen ein Abschnitt ins Feld kommt.
 *
 * Ein gespeicherter Block kann mehrere Absätze tragen (`<p>A</p><p>B</p>`) —
 * die bleiben einzelne Kinder, sonst ließe sich zwischen ihnen nicht mehr
 * getrennt formatieren. Alles andere (Klartext, loser Text mit `<br>`,
 * einzelne Inline-Auszeichnung) kommt in EIN `div`: ein loser Textknoten auf
 * oberster Ebene gehörte zu keinem Abschnitt, und der Browser hängte ihn beim
 * nächsten Enter irgendwo an.
 */
export function kinderFuer(html: string, istHtml: boolean): HTMLElement[] {
    const topf = document.createElement("div");
    if (!istHtml) {
        const eins = document.createElement("div");
        // Wie bisher am Block: Klartext bleibt Text, spitze Klammern bleiben
        // spitze Klammern.
        eins.textContent = html;
        if (!html) eins.appendChild(document.createElement("br"));
        return [eins];
    }
    topf.innerHTML = reinigeBlockHtml(html);
    const kinder = Array.from(topf.childNodes);
    const nurBloecke =
        kinder.length > 0 &&
        kinder.every(
            (n) =>
                (n.nodeType === Node.TEXT_NODE && (n.textContent ?? "").trim() === "") ||
                (n.nodeType === Node.ELEMENT_NODE &&
                    ["DIV", "P", "UL", "OL", "BLOCKQUOTE"].includes((n as Element).tagName)),
        );
    if (nurBloecke) {
        const out = kinder.filter((n) => n.nodeType === Node.ELEMENT_NODE) as HTMLElement[];
        if (out.length) return out;
    }
    const eins = document.createElement("div");
    while (topf.firstChild) eins.appendChild(topf.firstChild);
    if (!eins.childNodes.length) eins.appendChild(document.createElement("br"));
    return [eins];
}

/** Der Text im leeren Feld — steht als Attribut, das CSS liest ihn. */
export const PLATZHALTER = "Notiz schreiben …";

/** Die Schreibstelle — ein leeres `div` ganz oben. */
export function schreibstelle(): HTMLElement {
    const el = document.createElement("div");
    el.setAttribute(KEY_ATTR, NEU);
    el.setAttribute("data-platzhalter", PLATZHALTER);
    el.appendChild(document.createElement("br"));
    return el;
}

/**
 * Das Feld EINMAL aufbauen: Schreibstelle, darunter die Abschnitte in
 * Anzeigereihenfolge (neueste zuerst).
 */
export function baueFeld(feld: HTMLElement, abschnitte: Abschnitt[]): void {
    feld.innerHTML = "";
    feld.appendChild(schreibstelle());
    for (const a of abschnitte) {
        for (const kind of kinderFuer(a.html, a.istHtml)) {
            kind.setAttribute(KEY_ATTR, a.key);
            feld.appendChild(kind);
        }
    }
}

/**
 * Stile, die der Browser beim Schreiben selbst setzt, sofort wieder abnehmen.
 *
 * Safari und Chrome geben neu getipptem Text gern die Optik seines Nachbarn
 * mit, als `<span style="color: …; white-space: nowrap">`. Steht davor eine
 * Erwähnung, schreibt man in deren Magenta weiter, und wegen `nowrap` läuft
 * der Satz rechts aus dem Feld, statt umzubrechen (T-384, auf dem iPad). Beim
 * Speichern streicht `reinigeBlockHtml` jedes `style` — nach dem Neuladen sah
 * die Notiz wieder normal aus. Hier verschwindet es schon beim Tippen, sodass
 * das Feld zeigt, was gespeichert wird.
 *
 * Ausnahme ist die eine Eigenschaft, die das Feld selbst setzt: `--kf` (die
 * Farbe des Kürzels am Rand) auf oberster Ebene.
 */
export function streifeFremdeStile(feld: HTMLElement): void {
    for (const el of Array.from(feld.querySelectorAll<HTMLElement>("[style]"))) {
        const kf = el.parentElement === feld ? el.style.getPropertyValue("--kf") : "";
        el.removeAttribute("style");
        if (kf) el.style.setProperty("--kf", kf);
    }
}

/** Was im Feld EIN Ding ist: Erwähnung, Bezug, Mailkarte, Bild im Hochladen. */
const EINGESETZT = ".nb-mention, [data-ref-art], .nb-bild-platzhalter";

/**
 * Eingesetztes sperren (`contenteditable="false"`), damit niemand hineinschreibt.
 *
 * Gesetzt wurde die Sperre bisher nur beim Einsetzen und beim Anzeigen der
 * Bezüge — eine GESPEICHERTE Erwähnung kam ohne sie zurück. Und Safari nimmt
 * sie beim Zusammenführen zweier Zeilen wieder ab. Ohne Sperre landet, was man
 * hinter der Erwähnung tippt, IN ihr: Magenta, ohne Umbruch (T-384). Deshalb
 * bei jedem Sauberziehen neu.
 */
export function sperreEingesetztes(wurzel: HTMLElement): void {
    for (const el of Array.from(wurzel.querySelectorAll(EINGESETZT))) {
        if (el.getAttribute("contenteditable") !== "false") el.setAttribute("contenteditable", "false");
    }
}

const ZEILE = new Set(["DIV", "P", "LI", "BLOCKQUOTE", "H1", "H2", "H3", "H4", "H5", "H6"]);

/**
 * Die Sperre kurz aufheben, wenn die Rücktaste am Zeilenanfang (bzw. Entf am
 * Zeilenende) gleich zwei Zeilen zusammenführt.
 *
 * Safari löscht beim Zusammenführen die GANZE untere Zeile, wenn sie mit einer
 * gesperrten Erwähnung beginnt — „@CLL bitte zurückrufen" war mit einem
 * Tastendruck weg (nachgestellt in WebKit; Chrome führt korrekt zusammen).
 * Ohne Sperre führt auch Safari ganz normal zusammen, samt Rückgängig.
 * Gesperrt wird danach wieder, über {@link normalisiere} bzw. den Aufrufer.
 *
 * Nur an der Zeilengrenze: Mitten in der Zeile sorgt die Sperre dafür, dass
 * die Rücktaste hinter einer Erwähnung sie als Ganzes nimmt statt Buchstabe
 * für Buchstabe. Liefert, ob entsperrt wurde.
 */
export function entsperreAnZeilengrenze(feld: HTMLElement, richtung: "zurueck" | "vor"): boolean {
    const sel = typeof window !== "undefined" ? window.getSelection?.() : null;
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return false;
    let n: Node | null = sel.anchorNode;
    if (!n || !feld.contains(n)) return false;
    while (n && n !== feld && !(n.nodeType === Node.ELEMENT_NODE && ZEILE.has((n as Element).tagName))) {
        n = n.parentNode;
    }
    if (!n || n === feld) return false;
    const zeile = n as HTMLElement;

    const marke = sel.getRangeAt(0);
    const rest = aktivesDokument().createRange();
    rest.selectNodeContents(zeile);
    if (richtung === "zurueck") rest.setEnd(marke.startContainer, marke.startOffset);
    else rest.setStart(marke.endContainer, marke.endOffset);
    if (rest.toString() !== "" || rest.cloneContents().querySelector(`${EINGESETZT}, img`)) return false;

    const gesperrt = Array.from(feld.querySelectorAll(EINGESETZT)).filter(
        (el) => el.getAttribute("contenteditable") === "false",
    );
    for (const el of gesperrt) el.removeAttribute("contenteditable");
    return gesperrt.length > 0;
}

/**
 * Das Feld nach jedem Tastendruck sauber ziehen — die einzige Stelle, an der
 * `data-nk` gesetzt wird.
 *
 *  0. Vom Browser gesetzte Stile fallen weg ({@link streifeFremdeStile}),
 *     Eingesetztes ist gesperrt ({@link sperreEingesetztes}).
 *  1. Lose Textknoten auf oberster Ebene bekommen eine Hülle. Ohne sie gäbe es
 *     Text, der zu keinem Abschnitt gehört.
 *  2. Die Schlüssel laufen durch {@link zuordnen} — die eine Regel für
 *     Einfügen aus Word, verschobene Absätze und das, was Enter hinterlässt.
 *  3. Ganz oben steht IMMER die Schreibstelle. Wer sie wegräumt, bekommt eine
 *     neue: ohne sie ließe sich nichts Neues mehr schreiben.
 *  4. Das Zeichen am Rand steht nur am ERSTEN Kind eines Abschnitts.
 */
export function normalisiere(feld: HTMLElement, marke: (key: string) => RandMarke): void {
    streifeFremdeStile(feld);
    sperreEingesetztes(feld);
    huelleLoseKnoten(feld);

    let kinder = Array.from(feld.children) as HTMLElement[];
    if (!kinder.length) {
        feld.appendChild(schreibstelle());
        kinder = Array.from(feld.children) as HTMLElement[];
    }

    const keys = zuordnen(kinder.map((k) => k.getAttribute(KEY_ATTR)));
    kinder.forEach((k, i) => k.setAttribute(KEY_ATTR, keys[i]!));

    // Die Schreibstelle gehört nach oben — und zwar immer.
    if (keys[0] !== NEU) {
        feld.insertBefore(schreibstelle(), feld.firstChild);
        kinder = Array.from(feld.children) as HTMLElement[];
        keys.unshift(NEU);
    }

    let vorher: string | null = null;
    const gesperrt = new Map<string, boolean>();
    kinder.forEach((k, i) => {
        const key = keys[i]!;
        const erstes = key !== vorher;
        vorher = key;
        // Ein Handschrift-Block (reMarkable) ist ganz gesperrt, über alle
        // seine Absätze — ein `contenteditable="false"` je Kind, denn das Feld
        // selbst bleibt beschreibbar.
        if (!gesperrt.has(key)) gesperrt.set(key, key !== NEU && marke(key)?.gesperrt === true);
        if (gesperrt.get(key)) {
            k.setAttribute("contenteditable", "false");
            k.setAttribute(GESPERRT_ATTR, "true");
        } else if (k.hasAttribute(GESPERRT_ATTR)) {
            k.removeAttribute("contenteditable");
            k.removeAttribute(GESPERRT_ATTR);
        }
        if (key === NEU) {
            // Der Platzhalter hängt an `data-leer` — nicht an `:empty`: im
            // leeren contentEditable steht ein `<br>`, und `:empty` griffe
            // danach nie wieder.
            k.setAttribute("data-platzhalter", PLATZHALTER);
            // Eine frisch begonnene Liste ist kein leeres Feld — der Platzhalter
            // läge sonst über dem ersten, noch leeren Punkt.
            const leer = (k.textContent ?? "").trim() === "" && !k.querySelector("img, li");
            k.setAttribute("data-leer", leer && i === 0 ? "true" : "false");
        } else {
            k.removeAttribute("data-platzhalter");
            k.removeAttribute("data-leer");
        }
        // Der ANFANG eines Abschnitts bekommt Luft nach oben — eine Leerzeile
        // zwischen zwei Notizen (PO 28.09.2026: „neue Notiz automatisch Zeile
        // Shift Zeile, also darüber eine Zeile machen"). Als Attribut für das
        // CSS, nicht als `<br>` im Text: eine gespeicherte Leerzeile stünde in
        // jeder Notiz, im Archiv, in der Suche und im Export.
        if (erstes && key !== NEU && i > 0) k.setAttribute("data-anfang", "true");
        else k.removeAttribute("data-anfang");
        const m = erstes ? marke(key) : null;
        if (m) {
            k.setAttribute("data-kuerzel", m.kuerzel);
            k.setAttribute("title", m.titel);
            k.style.setProperty("--kf", m.farbe);
        } else {
            k.removeAttribute("data-kuerzel");
            k.removeAttribute("title");
            k.style.removeProperty("--kf");
        }
        // Das Datum über der Notiz (T-407, 27.10.2026: „Besser vorne einfach
        // das Datum dazuschreiben statt anzuzeigen, wenn man mit der Maus
        // drüber geht"). Es steht in der Leerzeile aus `data-anfang` — als
        // `::after` aus dem Attribut, also kein Knoten im Text: weder
        // löschbar noch gespeichert. Nur am Anfang eines Abschnitts; einem
        // Folgeabsatz, dem der Browser es beim Enter mitkopiert, wird es
        // wieder abgenommen.
        if (m?.zeit && k.hasAttribute("data-anfang")) k.setAttribute("data-zeit", m.zeit);
        else k.removeAttribute("data-zeit");
    });
}

/** Was auf oberster Ebene ein Absatz ist — alles andere ist Zeileninhalt. */
const BLOCK_TAGS = new Set([
    "DIV", "P", "UL", "OL", "BLOCKQUOTE", "PRE", "TABLE", "FIGURE",
    "H1", "H2", "H3", "H4", "H5", "H6", "HR",
]);

/**
 * Lose Zeileninhalte auf oberster Ebene in EINE Hülle je Lauf packen.
 *
 * Text, `<br>`, `<b>`, `<span>` … direkt im Feld gehören zu keinem Abschnitt.
 * Sie entstehen, wenn der Browser eine Liste auflöst (Rücktaste oder Enter im
 * ersten Listenpunkt, PO 28.09.2026: „Aufzählung konnte ich nicht löschen")
 * oder wenn etwas eingefügt wird. Bis zum 28.09.2026 bekam jeder lose
 * Textknoten seine eigene Hülle, `<br>` und Auszeichnung blieben nackt — aus
 * einer Zeile „<b>fett</b> Text" wurden zwei Absätze und ein leerer.
 */
export function huelleLoseKnoten(feld: HTMLElement): void {
    // Das Umhängen eines Knotens nimmt der Auswahl ihren Halt (der Browser
    // setzt sie beim Entfernen auf das Feld zurück) — also merken und danach
    // am selben Knoten wieder setzen. Der Knoten bleibt derselbe, er steht nur
    // eine Ebene tiefer.
    const sel = typeof window !== "undefined" ? window.getSelection?.() : null;
    const merk =
        sel && sel.rangeCount > 0 && feld.contains(sel.anchorNode)
            ? { knoten: sel.anchorNode!, offset: sel.anchorOffset, zu: sel.isCollapsed }
            : null;
    let bewegt = false;
    let lauf: HTMLElement | null = null;
    for (const n of Array.from(feld.childNodes)) {
        const istBlock = n.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has((n as Element).tagName);
        if (istBlock) {
            lauf = null;
            continue;
        }
        if (n.nodeType !== Node.TEXT_NODE && n.nodeType !== Node.ELEMENT_NODE) {
            n.remove();
            continue;
        }
        if (!lauf && n.nodeType === Node.TEXT_NODE && (n.textContent ?? "").trim() === "") {
            n.remove();
            continue;
        }
        if (!lauf) {
            lauf = document.createElement("div");
            n.parentNode!.insertBefore(lauf, n);
        }
        lauf.appendChild(n);
        bewegt = true;
    }
    if (bewegt && merk && merk.zu && sel && merk.knoten.isConnected) {
        try {
            const r = aktivesDokument().createRange();
            r.setStart(merk.knoten, merk.offset);
            r.collapse(true);
            sel.removeAllRanges();
            sel.addRange(r);
        } catch {
            /* Offset passt nicht mehr — dann bleibt die Auswahl, wie sie ist. */
        }
    }
}

/**
 * Die vorübergehende Markierung der Sprungmarke einer Mitteilung (User
 * 01.11.2026, `vorgang/v4/sprungmarke.ts`). Sie sitzt für ein paar Sekunden
 * an einem Abschnitt bzw. einer markierten Stelle IM Feld — gespeichert werden
 * darf sie nie, sonst leuchtete die Stelle bei jedem Öffnen wieder auf.
 */
export const SPRUNG_MARKE_KLASSE = "vg4-sprung-marke";

function ohneSprungMarke(el: HTMLElement): void {
    const alle = [el, ...Array.from(el.querySelectorAll<HTMLElement>(`.${SPRUNG_MARKE_KLASSE}`))];
    for (const x of alle) {
        if (!x.classList?.contains(SPRUNG_MARKE_KLASSE)) continue;
        x.classList.remove(SPRUNG_MARKE_KLASSE);
        if (!x.getAttribute("class")) x.removeAttribute("class");
    }
}

/** Das HTML eines Kindes, wie es gespeichert würde — ohne die Anzeige-Attribute. */
function gespeichertesHtml(kinder: HTMLElement[], eindampfen: (html: string) => string): string {
    const topf = document.createElement("div");
    for (const k of kinder) {
        const kopie = k.cloneNode(true) as HTMLElement;
        ohneSprungMarke(kopie);
        kopie.removeAttribute(KEY_ATTR);
        kopie.removeAttribute("data-kuerzel");
        kopie.removeAttribute("data-platzhalter");
        kopie.removeAttribute("data-leer");
        kopie.removeAttribute("data-anfang");
        kopie.removeAttribute("data-zeit");
        if (kopie.hasAttribute(GESPERRT_ATTR)) {
            kopie.removeAttribute(GESPERRT_ATTR);
            kopie.removeAttribute("contenteditable");
        }
        kopie.removeAttribute("title");
        kopie.style.removeProperty("--kf");
        if (!kopie.getAttribute("style")) kopie.removeAttribute("style");
        topf.appendChild(kopie);
    }
    return reinigeBlockHtml(eindampfen(topf.innerHTML));
}

/**
 * Was im Feld steht, Abschnitt für Abschnitt — der Stand, gegen den verglichen
 * und den gespeichert wird.
 *
 * `eindampfen` macht aus Karten und Bildern wieder Marker (`entwaesserBezuege`,
 * `entwaesserBilder`); die Komponente reicht es durch, damit dieses Modul ohne
 * den Bild-Cache auskommt.
 */
export function leseAbschnitte(
    feld: HTMLElement,
    eindampfen: (html: string) => string = (h) => h,
): AbschnittStand[] {
    const out: AbschnittStand[] = [];
    let aktuell: { key: string; kinder: HTMLElement[] } | null = null;
    for (const kind of Array.from(feld.children) as HTMLElement[]) {
        const key = kind.getAttribute(KEY_ATTR) ?? NEU;
        if (aktuell && aktuell.key === key) aktuell.kinder.push(kind);
        else {
            if (aktuell) out.push({ key: aktuell.key, html: gespeichertesHtml(aktuell.kinder, eindampfen) });
            aktuell = { key, kinder: [kind] };
        }
    }
    if (aktuell) out.push({ key: aktuell.key, html: gespeichertesHtml(aktuell.kinder, eindampfen) });
    return out;
}

/** Die Kinder EINES Abschnitts im Feld, in der Reihenfolge, in der sie stehen. */
export function kinderVon(feld: HTMLElement, key: string): HTMLElement[] {
    return (Array.from(feld.children) as HTMLElement[]).filter(
        (k) => (k.getAttribute(KEY_ATTR) ?? NEU) === key,
    );
}

/**
 * ÄNDERUNGEN VON AUSSEN EINTRAGEN, ohne dem Schreibenden den Cursor zu nehmen.
 *
 * Das Feld wird nie von React neu gezeichnet — ein `contentEditable`, in das
 * man hineinrendert, verliert bei jedem Durchlauf die Einfügemarke. Was ein
 * anderer Browser geschrieben hat, muss trotzdem ankommen; bis zum 20.09.2026
 * zeigte ein stehender Block fremde Änderungen ÜBERHAUPT nicht (erst nach dem
 * Neuladen der Seite).
 *
 * Angefasst wird nur, was hier unberührt ist:
 *  · ein NEUER Abschnitt wird eingehängt — an seinem Platz in der
 *    Anzeigereihenfolge;
 *  · ein GEÄNDERTER nur, wenn sein Stand hier noch der gespeicherte ist —
 *    seit dem 28.09.2026 auch dann, wenn der Cursor darin steht (PO: „muss in
 *    Echtzeit bei den anderen Bearbeitern angezeigt werden"). Wer nur darin
 *    steht, ohne zu tippen, sieht sonst nie, was die Kollegin schreibt, und
 *    überschriebe es beim nächsten eigenen Wort mit seinem alten Stand. Der
 *    Cursor bleibt dabei an seiner Textstelle;
 *  · ein VERSCHWUNDENER wird entfernt, wenn er hier unberührt ist.
 *
 * Alles andere bleibt stehen: Der eigene, noch nicht gespeicherte Satz wiegt
 * schwerer als die Aktualität der Anzeige — er ließe sich nicht wiederholen.
 */
export function gleicheAb(
    feld: HTMLElement,
    abschnitte: Abschnitt[],
    stand: Map<string, string>,
    opts: {
        eindampfen?: (html: string) => string;
        aufblasen?: (kind: HTMLElement) => void;
        marke: (key: string) => RandMarke;
    },
): void {
    const eindampfen = opts.eindampfen ?? ((h: string) => h);
    const imFeld = new Map<string, HTMLElement[]>();
    for (const s of leseAbschnitte(feld, eindampfen)) imFeld.set(s.key, kinderVon(feld, s.key));
    const standVon = leseAbschnitte(feld, eindampfen);
    const htmlImFeld = new Map(standVon.map((s) => [s.key, s.html]));
    const cursorIn = fokussierterKey(feld);

    const sollKeys = abschnitte.map((a) => a.key);
    /*
     * VERGLICHEN WIRD IN DERSELBEN FORM, in der das Feld liest.
     *
     * Der Server liefert den Text so, wie er gespeichert wurde — Klartext aus
     * dem Bestand etwa als nacktes „Hallo", das Feld liest denselben Abschnitt
     * als `<div>Hallo</div>`. Wer beides roh vergleicht, hält JEDEN solchen
     * Abschnitt bei jedem Nachladen für „von außen geändert", zeichnet ihn neu
     * und merkt sich die Rohform als Stand — und das nächste Speichern sieht
     * dann einen Unterschied, schreibt die Notiz einer Kollegin zurück und
     * trägt den Schreibenden als Bearbeiter ein, ohne dass er sie angefasst
     * hat. Deshalb läuft das Soll durch dieselbe Aufbereitung wie das Feld.
     */
    const soll = new Map(abschnitte.map((a) => [a.key, sollHtml(a, eindampfen)]));

    // 1. Entfernen, was es nicht mehr gibt — nur wenn hier nichts daran hängt.
    for (const [key, kinder] of imFeld) {
        if (key === NEU || sollKeys.includes(key)) continue;
        if (key === cursorIn) continue;
        if (htmlImFeld.get(key) !== stand.get(key)) continue;
        for (const k of kinder) k.remove();
        stand.delete(key);
    }

    // 2. Ändern und Einfügen — von hinten nach vorn, damit jeder neue
    //    Abschnitt den bereits einsortierten Nachbarn als Anker hat.
    for (let i = abschnitte.length - 1; i >= 0; i--) {
        const a = abschnitte[i]!;
        const kinder = kinderVon(feld, a.key);
        const neueKinder = () => {
            const ks = kinderFuer(a.html, a.istHtml);
            for (const k of ks) {
                k.setAttribute(KEY_ATTR, a.key);
                opts.aufblasen?.(k);
            }
            return ks;
        };
        if (!kinder.length) {
            // Einhängen VOR dem nächsten bekannten Nachbarn, sonst ans Ende.
            const nach = abschnitte.slice(i + 1).find((b) => kinderVon(feld, b.key).length);
            const anker = nach ? kinderVon(feld, nach.key)[0]! : null;
            for (const k of neueKinder()) feld.insertBefore(k, anker);
            stand.set(a.key, soll.get(a.key)!);
            continue;
        }
        const hier = htmlImFeld.get(a.key);
        // Ein gesperrter Block (reMarkable) wird hier nie bearbeitet — weicht
        // er ab, hat nur der Browser sein HTML umgeformt. Der Server gewinnt.
        if (hier !== stand.get(a.key) && !a.gesperrt) continue; // hier geändert, noch nicht gespeichert
        if (hier === soll.get(a.key)) continue; // nichts Neues
        if (a.key === cursorIn) {
            // Der Abschnitt, in dem der Cursor steht, ist hier UNVERÄNDERT
            // (sonst hätte die Zeile darüber abgebrochen) — die Änderung von
            // außen kommt also an, und der Cursor bleibt an seiner Textstelle.
            const offset = cursorOffsetIn(feld, a.key);
            const ks = neueKinder();
            kinder[0]!.replaceWith(...ks);
            for (const k of kinder.slice(1)) k.remove();
            setzeCursorOffset(ks, offset);
        } else {
            const ks = neueKinder();
            kinder[0]!.replaceWith(...ks);
            for (const k of kinder.slice(1)) k.remove();
        }
        stand.set(a.key, soll.get(a.key)!);
    }

    normalisiere(feld, opts.marke);
}

/** Das Soll eines Abschnitts in der Form, in der {@link leseAbschnitte} liest. */
function sollHtml(a: Abschnitt, eindampfen: (html: string) => string): string {
    return gespeichertesHtml(kinderFuer(a.html, a.istHtml), eindampfen);
}

/** Wie viele Zeichen Text im Abschnitt `key` VOR der Einfügemarke stehen. */
function cursorOffsetIn(feld: HTMLElement, key: string): number {
    const sel = window.getSelection?.();
    const kinder = kinderVon(feld, key);
    if (!sel || sel.rangeCount === 0 || !kinder.length) return 0;
    const r = aktivesDokument().createRange();
    r.setStartBefore(kinder[0]!);
    const marke = sel.getRangeAt(0);
    try {
        r.setEnd(marke.startContainer, marke.startOffset);
    } catch {
        return 0;
    }
    return r.toString().length;
}

/** Die Einfügemarke nach `offset` Zeichen in die neuen Kinder setzen. */
function setzeCursorOffset(kinder: HTMLElement[], offset: number): void {
    const sel = window.getSelection?.();
    if (!sel || !kinder.length) return;
    let rest = offset;
    for (const kind of kinder) {
        const gang = document.createTreeWalker(kind, NodeFilter.SHOW_TEXT);
        let n = gang.nextNode() as Text | null;
        while (n) {
            const len = n.data.length;
            if (rest <= len) {
                const r = aktivesDokument().createRange();
                r.setStart(n, rest);
                r.collapse(true);
                sel.removeAllRanges();
                sel.addRange(r);
                return;
            }
            rest -= len;
            n = gang.nextNode() as Text | null;
        }
    }
    // Der Text ist kürzer geworden: ans Ende des Abschnitts.
    const r = aktivesDokument().createRange();
    r.selectNodeContents(kinder[kinder.length - 1]!);
    r.collapse(false);
    sel.removeAllRanges();
    sel.addRange(r);
}

/**
 * STEHT DIE EINFÜGEMARKE AN EINER ABSCHNITTSGRENZE, und würde die Taste sie
 * überschreiten?
 *
 * ══ WARUM DAS EINE GRENZE BLEIBEN MUSS ═════════════════════════════════════
 * Das Feld liest sich als EIN Text, und genau so soll es sich bedienen lassen.
 * Eine Geste ist trotzdem verboten: die Rücktaste am Anfang eines Abschnitts.
 * Der Browser verschmilzt ihn dann mit dem darüber — gemessen in Chromium
 * behält der OBERE Absatz seine Zuordnung, der Text des unteren wandert
 * hinein. Aus der Notiz einer Kollegin würde damit still ein Stück MEINER
 * Notiz, und ihre Zeile verschwände aus der Akte, ohne dass jemand etwas
 * gelöscht hätte.
 *
 * Eine Akte darf ihre Urheberschaft nicht beim Tippen verlieren. Gesperrt ist
 * deshalb GENAU diese eine Bewegung (und ihr Gegenstück, die Entf-Taste am
 * Ende eines Abschnitts). Alles andere bleibt: Pfeiltasten laufen durch,
 * Markieren geht über Grenzen, geschrieben wird überall, und INNERHALB eines
 * Abschnitts verschmelzen Absätze wie in jedem Editor.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function anAbschnittsGrenze(feld: HTMLElement, richtung: "zurueck" | "vor"): boolean {
    const sel = typeof window !== "undefined" ? window.getSelection?.() : null;
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return false;
    const kinder = Array.from(feld.children) as HTMLElement[];
    let n: Node | null = sel.anchorNode;
    if (!n || !feld.contains(n)) return false;
    while (n && n.parentNode !== feld) n = n.parentNode;
    const kind = n as HTMLElement | null;
    if (!kind) return false;
    const i = kinder.indexOf(kind);
    if (i < 0) return false;

    const nachbar = richtung === "zurueck" ? kinder[i - 1] : kinder[i + 1];
    // Kein Nachbar = kein Übertritt; derselbe Abschnitt = erlaubt.
    if (!nachbar) return false;
    if (nachbar.getAttribute(KEY_ATTR) === kind.getAttribute(KEY_ATTR)) return false;

    // Steht die Marke wirklich am Rand des Kindes? `Range` bis zum Anfang bzw.
    // Ende vergleichen — Textknoten, Auszeichnung und `<br>` liegen dazwischen.
    const pruef = aktivesDokument().createRange();
    pruef.selectNodeContents(kind);
    const marke = sel.getRangeAt(0);
    if (richtung === "zurueck") {
        pruef.setEnd(marke.startContainer, marke.startOffset);
        return pruef.toString() === "";
    }
    pruef.setStart(marke.endContainer, marke.endOffset);
    return pruef.toString() === "";
}

/**
 * EINE MARKIERUNG ÜBER MEHRERE ABSCHNITTE LÖSCHEN, ohne sie zu verschmelzen.
 *
 * Dieselbe Gefahr wie bei {@link anAbschnittsGrenze}, nur über die Auswahl:
 * Ein Dreifachklick markiert die Zeile BIS ZUM ANFANG der nächsten Notiz. Löscht
 * der Browser das, zieht er die nächste Notiz in die markierte hinein — im
 * Browser nachgestellt (T-464): Wer seine eigene Notiz so löschte, speicherte
 * damit den Text der Kollegin unter seinem Namen und bekam die Frage, ob
 * DEREN Notiz gelöscht werden soll.
 *
 * Deshalb verliert hier jeder berührte Abschnitt nur seinen markierten Teil.
 * Ganz markierte Abschnitte bleiben als leere Zeile stehen (leer heißt
 * gelöscht, beim Verlassen des Feldes — mit Rückfrage, wenn es fremde sind);
 * gesperrte (reMarkable) bleiben unberührt. Die Einfügemarke steht danach am
 * Anfang der Markierung.
 *
 * `false` = nichts getan (keine Markierung, oder sie liegt in EINEM
 * Abschnitt — dort darf der Browser wie in jedem Editor löschen).
 */
export function loescheAuswahlUeberAbschnitte(feld: HTMLElement): boolean {
    const sel = typeof window !== "undefined" ? window.getSelection?.() : null;
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return false;
    const auswahl = sel.getRangeAt(0);
    if (!feld.contains(auswahl.startContainer) || !feld.contains(auswahl.endContainer)) return false;
    const kindVon = (knoten: Node, offset: number): HTMLElement | null => {
        // Steht eine Grenze direkt im Feld (zwischen zwei Kindern), zählt das Kind dahinter.
        if (knoten === feld) return (feld.children[Math.min(offset, feld.children.length - 1)] as HTMLElement) ?? null;
        let n: Node | null = knoten;
        while (n && n.parentNode !== feld) n = n.parentNode;
        return (n as HTMLElement | null) ?? null;
    };
    const kinder = Array.from(feld.children) as HTMLElement[];
    const von = kindVon(auswahl.startContainer, auswahl.startOffset);
    const bis = kindVon(auswahl.endContainer, auswahl.endOffset);
    if (!von || !bis) return false;
    const i0 = kinder.indexOf(von);
    const i1 = kinder.indexOf(bis);
    if (i0 < 0 || i1 < 0) return false;
    const schluessel = (k: HTMLElement) => k.getAttribute(KEY_ATTR) ?? NEU;
    const beteiligt = kinder.slice(i0, i1 + 1);
    const dok = aktivesDokument();
    if (new Set(beteiligt.map(schluessel)).size < 2) {
        // EIN Abschnitt, aber ganz markiert: WebKit (Safari, iPad) wirft dann
        // die Zeile samt Schlüssel weg und legt eine neue ohne an — die fiele
        // dem Abschnitt DARÜBER zu, der so eine leere Zeile als „Änderung"
        // bekäme (im Browser nachgestellt). Teilmarkierungen löscht der
        // Browser wie in jedem Editor.
        const deckt =
            ohneInhalt(dok, von, 0, auswahl.startContainer, auswahl.startOffset) &&
            ohneInhalt(dok, auswahl.endContainer, auswahl.endOffset, bis, bis.childNodes.length);
        const alleKinder = kinderVon(feld, schluessel(von));
        if (!deckt || alleKinder[0] !== von || alleKinder[alleKinder.length - 1] !== bis || schluessel(von) === NEU) {
            return false;
        }
    }

    const ganz: HTMLElement[] = [];
    // Von hinten nach vorn: so verschiebt das Löschen keine Grenze, die noch kommt.
    for (let i = beteiligt.length - 1; i >= 0; i--) {
        const kind = beteiligt[i]!;
        if (kind.hasAttribute(GESPERRT_ATTR)) continue;
        const teil = dok.createRange();
        teil.selectNodeContents(kind);
        if (kind === von) teil.setStart(auswahl.startContainer, auswahl.startOffset);
        if (kind === bis) teil.setEnd(auswahl.endContainer, auswahl.endOffset);
        if (teil.collapsed || !hatInhalt(teil)) continue;
        const istGanz =
            ohneInhalt(dok, kind, 0, teil.startContainer, teil.startOffset) &&
            ohneInhalt(dok, teil.endContainer, teil.endOffset, kind, kind.childNodes.length);
        if (istGanz) ganz.push(kind);
        else teil.deleteContents();
    }
    // Ganz markierte Kinder: weg, solange ihr Abschnitt noch ein anderes Kind hat;
    // sonst bleibt eine leere Zeile mit dem Schlüssel — sie zählt als gelöscht.
    for (const key of new Set(ganz.map(schluessel))) {
        const weg = ganz.filter((k) => schluessel(k) === key);
        const bleibt = kinderVon(feld, key).some((k) => !weg.includes(k));
        weg.forEach((kind, i) => {
            if (bleibt || i > 0) kind.remove();
            else kind.replaceChildren(dok.createElement("br"));
        });
    }

    // Einfügemarke an den Anfang der Markierung.
    const ziel = von.isConnected ? von : (Array.from(feld.children) as HTMLElement[]).find((k) => !k.hasAttribute(GESPERRT_ATTR)) ?? null;
    if (ziel) {
        const r = dok.createRange();
        if (von.isConnected && feld.contains(auswahl.startContainer) && ziel.contains(auswahl.startContainer)) {
            r.setStart(auswahl.startContainer, Math.min(auswahl.startOffset, laengeVon(auswahl.startContainer)));
        } else {
            r.selectNodeContents(ziel);
        }
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
    }
    return true;
}

/** Steht in diesem Bereich etwas — Text oder ein Bild? */
const hatInhalt = (r: Range): boolean => r.toString() !== "" || !!r.cloneContents().querySelector?.("img");

/** Liegt zwischen den beiden Stellen kein Inhalt? (Grenzen zählen nicht, nur Text und Bilder.) */
function ohneInhalt(dok: Document, a: Node, ao: number, b: Node, bo: number): boolean {
    const r = dok.createRange();
    r.setStart(a, ao);
    r.setEnd(b, bo);
    return r.collapsed || !hatInhalt(r);
}

const laengeVon = (n: Node): number => (n.nodeType === Node.TEXT_NODE ? (n.textContent ?? "").length : n.childNodes.length);

/**
 * Einen Handgriff am DOM ausführen, OHNE dass dabei Text seinen Abschnitt
 * wechselt.
 *
 * Löst der Browser eine Liste auf (Rücktaste im ersten Punkt, Enter im leeren
 * Punkt), entsteht an ihrer Stelle ein neuer Absatz ohne `data-nk`. Die Regel
 * „ein Kind ohne Schlüssel gehört zum Abschnitt darüber" (`zuordnen`) gäbe ihn
 * dann dem NACHBARN, wenn die Liste am Anfang ihres Abschnitts stand — aus der
 * Aufzählung einer Kollegin würde still ein Stück der Notiz darüber. Deshalb
 * bekommt alles, was der Handgriff neu anlegt, den Schlüssel des Abschnitts,
 * in dem der Cursor vorher stand.
 *
 * Liefert, was `tu` zurückgibt.
 */
export function mitSchluesselErhalten<T>(feld: HTMLElement, tu: () => T): T {
    const key = fokussierterKey(feld);
    const vorher = new Set(Array.from(feld.children));
    const ergebnis = tu();
    if (key === null) return ergebnis;
    huelleLoseKnoten(feld);
    for (const kind of Array.from(feld.children)) {
        if (!vorher.has(kind) || !kind.hasAttribute(KEY_ATTR)) kind.setAttribute(KEY_ATTR, key);
    }
    return ergebnis;
}

/** In welchem Abschnitt steht die Einfügemarke? `null` = außerhalb des Feldes. */
export function fokussierterKey(feld: HTMLElement): string | null {
    const sel = typeof window !== "undefined" ? window.getSelection?.() : null;
    const knoten = sel?.anchorNode ?? null;
    if (!knoten || !feld.contains(knoten)) return null;
    let n: Node | null = knoten;
    while (n && n.parentNode !== feld) n = n.parentNode;
    if (!n || !(n as HTMLElement).getAttribute) return null;
    return (n as HTMLElement).getAttribute(KEY_ATTR) ?? NEU;
}
